import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { X, ZoomIn } from 'lucide-react';

const galleryImages = [
  {
    src: "https://customer-assets.emergentagent.com/job_703a842f-a54d-49a9-b401-d9b355165d79/artifacts/c6pwjyep_ayahuasca%20amazon%20trip.png",
    alt: "Amazon Rainforest at Twilight",
    span: "col-span-2 row-span-2"
  },
  {
    src: "https://images.unsplash.com/photo-1693754078640-8bd19929a488?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxiaW9sdW1pbmVzY2VudCUyMGFtYXpvbiUyMGp1bmdsZSUyMG5pZ2h0JTIwbmVvbnxlbnwwfHx8fDE3NzA0NTc2MTl8MA&ixlib=rb-4.1.0&q=85",
    alt: "Mystical Jungle",
    span: "col-span-1 row-span-1"
  },
  {
    src: "https://images.unsplash.com/photo-1644917142358-8fc82f745faf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxiaW9sdW1pbmVzY2VudCUyMGFtYXpvbiUyMGp1bmdsZSUyMG5pZ2h0JTIwbmVvbnxlbnwwfHx8fDE3NzA0NTc2MTl8MA&ixlib=rb-4.1.0&q=85",
    alt: "Sacred Space",
    span: "col-span-1 row-span-1"
  },
  {
    src: "https://images.unsplash.com/photo-1769203903362-4a8a307b1cdd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxiaW9sdW1pbmVzY2VudCUyMGFtYXpvbiUyMGp1bmdsZSUyMG5pZ2h0JTIwbmVvbnxlbnwwfHx8fDE3NzA0NTc2MTl8MA&ixlib=rb-4.1.0&q=85",
    alt: "Bioluminescent Forest",
    span: "col-span-1 row-span-2"
  },
  {
    src: "https://images.unsplash.com/photo-1601072790987-acaae68f2edc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxiaW9sdW1pbmVzY2VudCUyMGFtYXpvbiUyMGp1bmdsZSUyMG5pZ2h0JTIwbmVvbnxlbnwwfHx8fDE3NzA0NTc2MTl8MA&ixlib=rb-4.1.0&q=85",
    alt: "River Journey",
    span: "col-span-1 row-span-1"
  },
  {
    src: "https://images.unsplash.com/photo-1763096567223-05417bfc0486?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxhbmNpZW50JTIwZWd5cHRpYW4lMjBnb2xkJTIwdGV4dHVyZSUyMGRhcmt8ZW58MHx8fHwxNzcwNDU3NjI0fDA&ixlib=rb-4.1.0&q=85",
    alt: "Ancient Wisdom",
    span: "col-span-1 row-span-1"
  }
];

export const GallerySection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section id="gallery" className="py-24 relative" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="font-accent text-egyptian-gold text-sm tracking-[0.3em] mb-4">
            VISUAL JOURNEY
          </p>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl uppercase tracking-tight text-white mb-6">
            The Sacred <span className="neon-cyan">Landscape</span>
          </h2>
          <p className="font-body text-gray-400 text-lg max-w-2xl mx-auto">
            Glimpses of the mystical environment where ancient wisdom meets modern seekers. 
            Experience the beauty of the Amazon.
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[200px]">
          {galleryImages.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`gallery-item rounded-2xl cursor-pointer group ${image.span}`}
              onClick={() => setSelectedImage(image)}
              data-testid={`gallery-item-${index}`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover rounded-2xl"
              />
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-jungle-dark/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <ZoomIn className="w-8 h-8 text-neon-cyan mx-auto mb-2" />
                  <p className="font-heading text-white uppercase tracking-wider text-sm">
                    {image.alt}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
              onClick={() => setSelectedImage(null)}
              data-testid="lightbox-close-btn"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-8 left-1/2 -translate-x-1/2 font-heading text-white uppercase tracking-wider">
              {selectedImage.alt}
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
};
